import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { limparInscricaoCodigo } from '@/lib/inscricaoCodigo';
import { solicitarAvisosAposSucesso } from '@/lib/avisosSucesso';
import { buscarSituacaoInscrito, type SituacaoInscrito } from '@/lib/situacaoInscrito';
import SeloSituacaoInscrito from '@/components/feature/SeloSituacaoInscrito';
import {
  buscarSituacaoPresencial,
  type SituacaoDaInscricao,
  type SituacaoPresencial,
} from '@/lib/presencial';
import TextoRico from '@/components/base/TextoRico';
import ScreenHeader from '@/components/ui/ScreenHeader';
import Card from '@/components/ui/Card';
import PrimaryButton from '@/components/ui/PrimaryButton';
import SecondaryButton from '@/components/ui/SecondaryButton';
import SuasDatas from './components/SuasDatas';
import TelaPresencial from './components/TelaPresencial';
import { formatarData, formatarPeriodo, formatarValor } from '@/pages/evento/utils';
import type { EventoDetalhe, InscricaoConfirma, SaldoDetalhe, MeuEncontro } from './types';

function formatarDinheiro(centavos: number): string {
  const reais = Math.floor(centavos / 100);
  const cents = centavos % 100;
  return `R$ ${reais.toLocaleString('pt-BR')},${String(cents).padStart(2, '0')}`;
}

function LinhaDado({
  rotulo,
  destaque = false,
  children,
}: {
  rotulo: string;
  destaque?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2.5">
      <span className="shrink-0 text-[12px] font-semibold uppercase tracking-wide text-foreground-400">
        {rotulo}
      </span>
      <span
        className={`min-w-0 text-right text-[15px] font-bold ${
          destaque ? 'text-foreground-950' : 'text-foreground-800'
        }`}
      >
        {children}
      </span>
    </div>
  );
}

export default function MinhaInscricaoPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nova = searchParams.get('nova');
  // A ficha concluiu com a foto ainda subindo (ou com falha no envio).
  const fotoNaoEnviada = searchParams.get('foto') === '0';
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [naoEncontrado, setNaoEncontrado] = useState(false);
  const [inscrito, setInscrito] = useState<InscricaoConfirma | null>(null);
  const [evento, setEvento] = useState<EventoDetalhe | null>(null);
  const [saldo, setSaldo] = useState<SaldoDetalhe | null>(null);
  // O que ESTE inscrito pode ver (sigilo vem do banco).
  const [situacaoInscrito, setSituacaoInscrito] = useState<SituacaoInscrito | null>(null);
  const [carregandoSituacao, setCarregandoSituacao] = useState(true);
  const [minhasDatas, setMinhasDatas] = useState<MeuEncontro[]>([]);
  const [copiado, setCopiado] = useState(false);
  const [presencial, setPresencial] = useState<SituacaoDaInscricao | null>(null);
  // Depois que a pessoa vê o resultado da bipagem, mostra a inscrição normal.
  const [verNormal, setVerNormal] = useState(false);

  // Inscrição recém-enviada (?nova=1): o código do convite já cumpriu a função,
  // então limpamos — assim o cartão "Continuar sua inscrição" some do Início.
  useEffect(() => {
    if (nova === '1') limparInscricaoCodigo();
  }, [nova]);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(`/entrar?voltar=${encodeURIComponent(`/minha-inscricao/${codigo}`)}`, {
        replace: true,
      });
      return;
    }
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      setNaoEncontrado(false);
      setPresencial(null);
      setVerNormal(false);
      setSituacaoInscrito(null);
      setCarregandoSituacao(true);
      try {
        await garantirUsuario();

        const { data: inscritoData, error: errInscrito } = await supabase
          .from('vw_inscritos')
          .select('*')
          .eq('codigo', codigo)
          .maybeSingle();
        if (errInscrito) throw errInscrito;
        if (!inscritoData) {
          if (ativo) setNaoEncontrado(true);
          return;
        }

        const inscritoLinha = inscritoData as InscricaoConfirma;
        if (ativo) setInscrito(inscritoLinha);
        if (ativo) setCarregandoSituacao(true);

        // Sigilo: só depois desta resposta a tela mostra situação/valores.
        const situacaoCarregada = await buscarSituacaoInscrito(
          inscritoLinha.inscricao_id,
        ).catch(() => null);
        if (ativo) {
          setSituacaoInscrito(situacaoCarregada);
          setCarregandoSituacao(false);
        }

        const { data: eventoData, error: errEvento } = await supabase
          .from('eventos')
          .select('id, local_nome, cidade, uf, fim_em, texto_pagamento, vende_ingressos')
          .eq('id', inscritoLinha.evento_id)
          .maybeSingle();
        if (errEvento) throw errEvento;
        const eventoLinha = (eventoData as EventoDetalhe) || null;
        if (ativo) setEvento(eventoLinha);

        // Evento que vende ingresso: redireciona para o pedido, nunca mostra "Inscrição registrada!".
        if (eventoLinha?.vende_ingressos) {
          const { data: ingData } = await supabase
            .from('vw_meus_ingressos')
            .select('pedido_id')
            .eq('ingresso', codigo)
            .maybeSingle();
          const pedidoId = (ingData as { pedido_id: number } | null)?.pedido_id;
          if (pedidoId != null) {
            navigate(`/pedido/${pedidoId}`, { replace: true });
            return;
          }
        }

        // Inscrição presencial: a vaga é de quem a direção bipar primeiro.
        const sit = await buscarSituacaoPresencial(codigo ?? '').catch(() => null);
        if (ativo && sit?.presencial) setPresencial(sit);

        const { data: saldoData, error: errSaldo } = await supabase
          .from('vw_saldo_inscricao')
          .select('inscricao_id, pago_centavos, saldo_centavos')
          .eq('inscricao_id', inscritoLinha.inscricao_id)
          .maybeSingle();
        if (errSaldo) throw errSaldo;
        if (ativo) setSaldo((saldoData as SaldoDetalhe) || null);

        const { data: encData, error: errEnc } = await supabase
          .from('vw_meus_encontros')
          .select('*')
          .eq('inscricao_id', inscritoLinha.inscricao_id)
          .order('inicio_em');
        if (errEnc) throw errEnc;
        if (ativo) setMinhasDatas((encData as MeuEncontro[]) || []);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar sua inscrição.'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [codigo, navigate, pronto, user?.id]);

  // Logo depois de uma inscrição registrada: pede os avisos (1,5 s).
  // Nunca por cima do QR presencial enquanto a vaga ainda não foi validada.
  const presencialAguardando = presencial?.presencial && presencial.situacao === 'aguardando';
  useEffect(() => {
    if (inscrito?.evento && !presencialAguardando) solicitarAvisosAposSucesso(inscrito.evento);
  }, [inscrito?.evento, presencialAguardando]);

  async function copiarCodigo() {
    if (!inscrito) return;
    try {
      await navigator.clipboard.writeText(inscrito.codigo);
    } catch {
      // fallback silencioso
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[560px] px-5 pt-6">
          <div className="flex flex-col items-center">
            <div className="h-20 w-20 animate-pulse rounded-full bg-background-200/70" />
            <div className="mt-4 h-6 w-56 animate-pulse rounded bg-background-200/70" />
            <div className="mt-2 h-4 w-72 animate-pulse rounded bg-background-200/70" />
          </div>
          <div className="mt-6 h-64 animate-pulse rounded-[18px] bg-background-200/70" />
        </div>
      </div>
    );
  }

  if (naoEncontrado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-search-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não encontramos esta inscrição
          </h1>
          <PrimaryButton cheio className="mt-6" onClick={() => navigate('/minhas-inscricoes')}>
            Ver minhas inscrições
          </PrimaryButton>
        </Card>
      </div>
    );
  }

  if (erro || !inscrito) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não foi possível carregar
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-foreground-600">{erro}</p>
          <PrimaryButton cheio className="mt-6" onClick={() => window.location.reload()}>
            Tentar novamente
          </PrimaryButton>
        </Card>
      </div>
    );
  }

  // Inscrição presencial: no modo QR, a tela é o QR grande enquanto aguarda a
  // bipagem. No modo senha da fila (Campista), a mensagem final é sempre
  // "Deus te abençoe!" — sem vaga nem suplente. Depois mostra a inscrição normal.
  // Sigilo: quando ligado, nada de valor, "Como pagar" ou cobrança.
  const sigilo = situacaoInscrito?.sigilo === true;
  const situacaoReal = situacaoInscrito?.situacao ?? 'recebida';
  const listaEspera = !sigilo && situacaoReal === 'lista_espera';
  const mostrarValores = !sigilo && situacaoInscrito?.mostrar_valores !== false;

  const usaSenha = !!presencial?.presencial && presencial.usa_senha === true;
  // Com sigilo, só a tela da senha da fila aparece (ela nunca revela vaga).
  if (presencial?.presencial && !verNormal && (!sigilo || usaSenha)) {
    const sit = presencial.situacao;
    const telaSenha = usaSenha && (sit === 'vaga' || sit === 'lista_espera');
    if (sit === 'aguardando' || telaSenha) {
      return (
        <TelaPresencial
          codigo={inscrito.codigo}
          nome={inscrito.participante}
          eventoNome={inscrito.evento}
          situacaoInicial={sit as SituacaoPresencial}
          usaSenha={usaSenha}
          senha={presencial.senha}
          valorCentavos={inscrito.valor_centavos}
          status={inscrito.status}
          semFoto={fotoNaoEnviada}
          aoFicha={() => navigate(`/minha-inscricao/${inscrito.codigo}/ficha`)}
          aoConcluir={(resultado) => {
            // Reflete já na inscrição normal o resultado da fila (vaga/lista).
            setPresencial((prev) =>
              prev
                ? {
                    ...prev,
                    situacao: resultado.situacao,
                    ordem: resultado.ordem,
                    posicao_espera: resultado.posicao_espera,
                    validada_em: resultado.validada_em ?? prev.validada_em,
                  }
                : prev,
            );
            setVerNormal(true);
          }}
        />
      );
    }
  }

  const periodo = evento?.fim_em
    ? formatarPeriodo(inscrito.evento_inicio_em, evento.fim_em)
    : formatarData(inscrito.evento_inicio_em);

  const local = [evento?.local_nome, evento?.cidade, evento?.uf].filter(Boolean).join(', ');

  const participacao = inscrito.grupo
    ? `${inscrito.modalidade} — ${inscrito.grupo}`
    : inscrito.modalidade;

  const codigoCurto = inscrito.codigo.slice(0, 8).toUpperCase();

  const mostrarComoPagar =
    mostrarValores && inscrito.valor_centavos > 0 && !!evento?.texto_pagamento?.trim();

  const temPagamento = mostrarValores && !!saldo && saldo.pago_centavos > 0;

  return (
    <div className="min-h-screen bg-background-100 pb-28">
      <ScreenHeader titulo="Inscrição" voltar />

      <main className="mx-auto w-full max-w-[560px] px-5 pt-6">
        {/* Campista (senha da fila): a pessoa só vê "Inscrito" — vaga e
            suplente continuam apenas na tela da direção. */}
        {presencial?.presencial && usaSenha && !sigilo && (
          <div className="app-entra mb-5 flex items-center gap-3 rounded-[18px] bg-background-50 px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-500 text-white">
              <i className="ri-check-line text-xl" aria-hidden />
            </span>
            <span className="text-[15px] font-bold text-foreground-950">Inscrito</span>
          </div>
        )}

        {!usaSenha && !sigilo && presencial?.presencial && presencial.situacao === 'vaga' && (
          <div className="app-entra mb-5 flex items-center gap-3 rounded-[18px] bg-success-50 px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-500 text-white">
              <i className="ri-check-line text-xl" aria-hidden />
            </span>
            <span className="text-[15px] font-bold text-success-800">
              Vaga garantida
              {presencial.ordem != null ? ` · você é o nº ${presencial.ordem}` : ''}
            </span>
          </div>
        )}
        {!usaSenha &&
          !sigilo &&
          presencial?.presencial &&
          presencial.situacao === 'lista_espera' &&
          presencial.validada_em && (
            <div className="app-entra mb-5 flex items-center gap-3 rounded-[18px] bg-warning-50 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning-500 text-white">
                <i className="ri-time-line text-xl" aria-hidden />
              </span>
              <span className="text-[15px] font-bold text-warning-800">
                Lista de espera
                {presencial.posicao_espera != null
                  ? ` · ${presencial.posicao_espera}º na ordem da senha`
                  : ''}
              </span>
            </div>
          )}

        {fotoNaoEnviada && (
          <div className="app-entra mb-4 rounded-[18px] bg-warning-50 px-4 py-3 text-[13px] leading-relaxed text-warning-800">
            A foto não foi enviada — dá para mandar depois em Minhas inscrições.
          </div>
        )}

        <div className="app-entra text-center">
          <span
            className={`check-pop mx-auto flex h-20 w-20 items-center justify-center rounded-full text-white ${
              sigilo || carregandoSituacao
                ? 'bg-background-300'
                : listaEspera
                  ? 'bg-secondary-400'
                  : 'bg-success-500'
            }`}
            style={
              sigilo || carregandoSituacao || listaEspera
                ? undefined
                : { boxShadow: '0 10px 24px rgba(22,163,74,.28)' }
            }
          >
            <i
              className={
                sigilo || carregandoSituacao
                  ? 'ri-mail-line text-[38px]'
                  : listaEspera
                    ? 'ri-stack-line text-[38px]'
                    : 'ri-check-line text-[44px]'
              }
              aria-hidden
            />
          </span>
          <h1 className="mt-4 text-[22px] font-extrabold text-foreground-950">
            {sigilo || carregandoSituacao
              ? 'Inscrição recebida'
              : listaEspera
                ? 'Você entrou como suplente'
                : 'Inscrição registrada!'}
          </h1>
          <p className="mt-1 text-[15px] text-foreground-600">
            {sigilo || carregandoSituacao
              ? situacaoInscrito?.texto ||
                'Inscrição recebida. A direção vai entrar em contato com você.'
              : listaEspera
                ? 'As vagas acabaram e você entrou como suplente. Avisaremos se abrir uma vaga.'
                : `${inscrito.participante} está inscrito no ${inscrito.evento}.`}
          </p>
        </div>

        <Card className="app-entra mt-6 px-5 py-2">
          <div className="divide-y divide-background-200/70">
            <LinhaDado rotulo="Participante" destaque>
              {inscrito.participante}
            </LinhaDado>
            <LinhaDado rotulo="Evento">{inscrito.evento}</LinhaDado>
            <LinhaDado rotulo="Data">{periodo}</LinhaDado>
            {local && <LinhaDado rotulo="Local">{local}</LinhaDado>}
            <LinhaDado rotulo="Participação">{participacao}</LinhaDado>
            {mostrarValores && (
              <LinhaDado rotulo="Valor">{formatarValor(inscrito.valor_centavos)}</LinhaDado>
            )}
            <div className="flex items-center justify-between gap-3 py-3">
              <span className="text-[12px] font-semibold uppercase tracking-wide text-foreground-400">
                Código
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[15px] font-bold text-foreground-950">
                  {codigoCurto}
                </span>
                <button
                  type="button"
                  onClick={copiarCodigo}
                  className="aperta flex h-8 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-3 text-[13px] font-semibold text-foreground-700"
                >
                  <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
                  {copiado ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
          </div>
        </Card>

        {usaSenha && !sigilo ? (
          <div className="mt-4 flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-3 py-1.5 text-[13px] font-bold text-success-700">
              <i className="ri-check-line" aria-hidden />
              Inscrito
            </span>
          </div>
        ) : (
          <div className="mt-4 flex justify-center">
            <SeloSituacaoInscrito
              situacao={situacaoInscrito}
              carregando={carregandoSituacao}
              comTexto={sigilo || listaEspera || carregandoSituacao}
              className="text-center"
            />
          </div>
        )}

        {mostrarComoPagar && (
          <Card className="app-entra mt-4 p-5">
            <h2 className="text-[15px] font-bold text-foreground-950">Como pagar</h2>
            <TextoRico
              texto={evento?.texto_pagamento}
              className="mt-2 text-[15px] leading-relaxed text-foreground-700"
            />
          </Card>
        )}

        {temPagamento && (
          <Card className="app-entra mt-4 p-5">
            <div className="flex items-center justify-between gap-3 text-[14px]">
              <span className="text-foreground-600">Já recebido</span>
              <span className="font-bold text-success-600">
                {formatarDinheiro(saldo!.pago_centavos)}
              </span>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3 text-[14px]">
              <span className="text-foreground-600">Falta</span>
              <span className="font-bold text-foreground-950">
                {formatarDinheiro(saldo!.saldo_centavos)}
              </span>
            </div>
          </Card>
        )}

        <div className="app-entra mt-4">
          <SecondaryButton cheio onClick={() => navigate('/minhas-inscricoes')}>
            Ver minhas inscrições
          </SecondaryButton>
        </div>

        <SuasDatas encontros={minhasDatas} cidade={inscrito.evento_cidade} />
      </main>

      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-background-200 bg-background-50/95 backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto w-full max-w-[560px] px-5 py-3">
          <PrimaryButton
            cheio
            icone="ri-edit-line"
            onClick={() => navigate(`/minha-inscricao/${inscrito.codigo}/ficha`)}
          >
            Minha ficha
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// Selo de situação discreto (mantém o componente SeloSituacao onde couber).
function SeloInline({ status }: { status: string }) {
  const mapa: Record<string, { texto: string; classe: string; icone: string }> = {
    confirmada: { texto: 'Confirmada', classe: 'bg-success-50 text-success-700', icone: 'ri-check-line' },
    pendente: { texto: 'Aguardando pagamento', classe: 'bg-warning-50 text-warning-700', icone: 'ri-time-line' },
    isenta: { texto: 'Isenta', classe: 'bg-secondary-100 text-secondary-700', icone: 'ri-gift-line' },
    lista_espera: { texto: 'Lista de espera', classe: 'bg-warning-50 text-warning-700', icone: 'ri-time-line' },
    cancelada: { texto: 'Cancelada', classe: 'bg-danger-50 text-danger-700', icone: 'ri-close-circle-line' },
  };
  const cfg = mapa[status] ?? { texto: status, classe: 'bg-secondary-100 text-secondary-700', icone: 'ri-information-line' };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-bold ${cfg.classe}`}>
      <i className={cfg.icone} aria-hidden />
      {cfg.texto}
    </span>
  );
}